import { TimelinesStyles, TimeAxisStyles } from "../../TimelinesStyles";
import ErrorBoundary from "../../../SimpleComponents/Errors/ErrorBoundary";
import {
  useHideIfRangeTooLong,
  useGetPageFromPath,
  useProcessesInVisibleGroups,
  useVisibleGroups,
  useFocusRange,
  useTrendsPageFilterState,
} from "../../../../utils/customHooks/genericHooks";
import { useCustomerKey } from "utils/customHooks/useCustomerKey";
import { ONE_DAY_OF_MS } from "../../../../utils/timeUtils";
import { TooBigToLoadIndicator } from "../../../SimpleComponents/TooBigToLoadIndicator";
import { useIsDarkMode } from "store/atoms";
import { ProcessesColumn } from "./ProcessesColumn";
import { TimeAxisColumn } from "./TimeAxisColumn";
import { MetricsColumns } from "./MetricsColumns";
import { useVisibleMetrics } from "components/Dashboard/GroupMetrics/useVisibleMetrics";
import { getAllProcessesWithCurrentFilters } from "components/FlightTrends/trendsPageUtils";
import Spinner from "components/SimpleComponents/Spinner/Spinner";
import { useLocation } from "react-router";
import { useMediaQuery } from "@mui/material";
import { useAllWorkSchedulesPageProcesses } from "components/Dashboard/Tiles/GroupTile/groupTilesUtils";
import { useSelector } from "store/useSelector";
const MAX_ALLOWABLE_DAYS_BEFORE_HIDE_TIMELINE = 4;
const MAX_ALLOWABLE_DAYS_BEFORE_HIDE_TIMELINE_ON_TRENDS_PAGE = 120;

interface TimelinesProps {
  isCustomDashboardLayout?: boolean | null;
  processIdsOverride?: string[];
}

const Timelines = ({
  isCustomDashboardLayout = false,
  processIdsOverride = [],
}: TimelinesProps) => {
  const focusRange = useFocusRange();
  const ready = Boolean(focusRange);
  const processes = useProcessesInVisibleGroups();
  const visibleGroups = useVisibleGroups();
  const { isTrendsPage, isAllWorkSchedulesPage } = useGetPageFromPath();
  const {
    trendsPageFilterState,
    isTrendsPageFilteredByGroup,
    isTrendsPageFilteredByProcess,
  } = useTrendsPageFilterState();

  let processesFiltered =
    processIdsOverride.length > 0
      ? processes
          .filter(p => processIdsOverride.includes(p.id))
          // move the first process to the top
          .sort((a, b) =>
            a.id === processIdsOverride[0]
              ? -1
              : b.id === processIdsOverride[0]
                ? 1
                : 0
          )
      : !isTrendsPage
        ? processes
        : getAllProcessesWithCurrentFilters(
            trendsPageFilterState,
            processes,
            visibleGroups,
            isTrendsPageFilteredByGroup,
            isTrendsPageFilteredByProcess
          );
  const processesInSelectedGroups = useAllWorkSchedulesPageProcesses();
  if (isAllWorkSchedulesPage) {
    // filter processes down to only those in the selected groups
    processesFiltered = processesInSelectedGroups;
  }

  const { search } = useLocation();
  const maxFocusRangeDuration =
    (isTrendsPage
      ? MAX_ALLOWABLE_DAYS_BEFORE_HIDE_TIMELINE_ON_TRENDS_PAGE
      : MAX_ALLOWABLE_DAYS_BEFORE_HIDE_TIMELINE) *
    ONE_DAY_OF_MS *
    (search.includes("quickView=lastWeek") ? 8 : 1);
  const { shouldHide, handleShowAnyway } = useHideIfRangeTooLong({
    maxFocusRangeDuration,
  });

  const ckey = useCustomerKey();
  const processesSortOrder = visibleGroups[0]?.processesSortOrder ?? [];
  const customersProcesses = processesFiltered
    // filter out any other customer's processes
    // (only applies to people who can access multiple customers)
    .filter(proc => proc.ckey === ckey)
    .sort(
      (a, b) =>
        processesSortOrder.indexOf(a.id) - processesSortOrder.indexOf(b.id)
    );

  const visibleMetrics = useVisibleMetrics();
  const metricsColumns = visibleMetrics.TimelineColumn;
  const [isDarkMode] = useIsDarkMode();

  const isPrint = useMediaQuery("print");
  /** when there are  metrics displayed beside the timeline, in print preview, split the timeline onto a separate row */
  const printSplitIntoTwoRows = isPrint && !isTrendsPage;
  const hideMetricsColumn = window.location.search.includes(
    "hideMetricsColumn=true"
  );
  const isRetrotagging = useSelector(
    state => state.retrotag.isRetrotagPopperOpen
  );
  return (
    <ErrorBoundary id="Timelines">
      <TimelinesStyles
        id="timelineStyles"
        className={`TimelinesStyles tooltipOpen ${isRetrotagging ? "isRetrotagging" : ""}`}
        $isDarkMode={isDarkMode}
      >
        {!ready ? (
          <Spinner width={24} />
        ) : shouldHide && !isCustomDashboardLayout ? ( // on custom dashboards, always load the timeline
          <TooBigToLoadIndicator
            name={"timeline"}
            handleTryToLoad={handleShowAnyway}
            maxFocusRangeDuration={maxFocusRangeDuration}
          />
        ) : (
          <>
            <TimeAxisStyles
              $isDarkMode={isDarkMode}
              id="TimeAxisStyles"
              className={`TimeAxisStyles ${
                printSplitIntoTwoRows ? "splitTimeline" : ""
              }`}
            >
              <ErrorBoundary id="TimelinesContent">
                <div className="timelineLeftSide">
                  <ProcessesColumn processes={customersProcesses} />
                  <div className="timelineSpacer spacer" />
                  {
                    /* disable the metric columns on trends page  */
                    isTrendsPage || hideMetricsColumn ? null : (
                      <>
                        <MetricsColumns
                          metricsColumns={metricsColumns}
                          processes={customersProcesses}
                        />
                        {metricsColumns.length > 0 ? (
                          <div className="timelineSpacer spacer" />
                        ) : null}
                      </>
                    )
                  }
                </div>
                {printSplitIntoTwoRows ? null : (
                  <TimeAxisColumn
                    processes={customersProcesses}
                    isTrendsPage={isTrendsPage}
                  />
                )}
              </ErrorBoundary>
            </TimeAxisStyles>
            {printSplitIntoTwoRows ? (
              <TimeAxisStyles
                id="TimeAxisStyles"
                $isDarkMode={isDarkMode}
                className="TimeAxisStyles"
                style={{ marginTop: 32 }}
              >
                <ErrorBoundary id="TimelinesContent_print">
                  <ProcessesColumn processes={customersProcesses} />
                  <TimeAxisColumn
                    processes={customersProcesses}
                    isTrendsPage={isTrendsPage}
                  />
                </ErrorBoundary>
              </TimeAxisStyles>
            ) : null}
          </>
        )}
      </TimelinesStyles>
    </ErrorBoundary>
  );
};

export default Timelines;
